import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../../ui";
import { useCountryAddressConfig } from "../useCountryAddressConfig";
import countryList from "../data/countryList.json";
import { createBillingProfile } from "../billingApi";

interface SetUpBillingProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  legalName: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  city: string;
  stateRegion: string;
  postalCode: string;
  contactFullName: string;
  contactEmail: string;
}

const EMPTY: FormState = {
  legalName: "",
  addressLine1: "",
  addressLine2: "",
  country: "",
  city: "",
  stateRegion: "",
  postalCode: "",
  contactFullName: "",
  contactEmail: "",
};

const COUNTRIES = countryList as { code: string; name: string }[];

/**
 * The inline "Set up Billing Profile" side drawer. It opens over the builder
 * without navigating, so the campaign draft survives a save or cancel; on
 * success it clears the gate via onCreated. Collects company identity,
 * registered address, and the primary billing contact.
 */
export const SetUpBillingProfileDrawer = ({ open, onClose, onCreated }: SetUpBillingProfileDrawerProps) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);

  const { config } = useCountryAddressConfig(form.country);
  const countryName = useMemo(() => COUNTRIES.find((c) => c.code === form.country)?.name ?? "", [form.country]);

  const set = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.legalName.trim()) next.legalName = "Legal company name is required";
    if (!form.country.trim()) next.country = "Country is required";
    if (!form.addressLine1.trim()) next.addressLine1 = "Address line 1 is required";
    if (!form.city.trim()) next.city = "City is required";
    if (config.regionRequired && !form.stateRegion.trim()) next.stateRegion = `${config.regionLabel} is required`;
    if (config.postalRequired && !form.postalCode.trim()) next.postalCode = `${config.postalLabel} is required`;
    if (form.contactEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail))
      next.contactEmail = "Enter a valid email";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setSubmitError(null);
    if (!validate()) return;
    setIsSaving(true);
    try {
      await createBillingProfile({
        legalName: form.legalName.trim(),
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        stateRegion: form.stateRegion.trim() || null,
        postalCode: form.postalCode.trim() || null,
        country: form.country || null,
        ...(form.contactFullName.trim() || form.contactEmail.trim()
          ? {
              contacts: [
                {
                  fullName: form.contactFullName.trim(),
                  email: form.contactEmail.trim(),
                  isPrimary: true,
                },
              ],
            }
          : {}),
      });
      setForm(EMPTY);
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save billing profile");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} side="right" aria-label="Set up Billing Profile" zIndex={1500}>
      <SheetHeader>
        <SheetTitle>Set up Billing Profile</SheetTitle>
        <SheetDescription>Enter the company details that appear on your invoices.</SheetDescription>
      </SheetHeader>

      <SheetBody className="flex flex-col gap-6">
        <Section
          step={1}
          title="Company identity"
          subtitle="How this entity appears on every invoice and tax document."
        >
          <Field label="Legal company name" required error={errors.legalName} hint="How it should appear on invoices.">
            <Input
              value={form.legalName}
              onChange={(e) => set("legalName", e.target.value)}
              placeholder="e.g. Company Inc."
              data-cypress-id="billing-profile-legal-name"
              cypress-id="billing-profile-legal-name"
            />
          </Field>
        </Section>

        <Section
          step={2}
          title="Registered address"
          subtitle="We use your country to determine the right tax fields and state list."
        >
          <Field label="Country" required error={errors.country}>
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={countryOpen}
                  className={cn("w-full justify-between font-normal", !form.country && "text-muted-foreground")}
                  data-cypress-id="billing-profile-country-trigger"
                  cypress-id="billing-profile-country-trigger"
                >
                  {countryName || "Select a country"}
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[1600] w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search country…" />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRIES.map((c) => (
                        <CommandItem
                          key={c.code}
                          value={c.name}
                          onSelect={() => {
                            set("country", c.code);
                            set("stateRegion", "");
                            setCountryOpen(false);
                          }}
                        >
                          <span className="flex-1">{c.name}</span>
                          <Check className={cn("h-4 w-4", form.country === c.code ? "opacity-100" : "opacity-0")} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          <Field label="Address line 1" required error={errors.addressLine1}>
            <Input
              value={form.addressLine1}
              onChange={(e) => set("addressLine1", e.target.value)}
              placeholder="Number and street name"
              data-cypress-id="billing-profile-address1"
              cypress-id="billing-profile-address1"
            />
          </Field>
          <Field label="Address line 2 (optional)">
            <Input
              value={form.addressLine2}
              onChange={(e) => set("addressLine2", e.target.value)}
              placeholder="Apartment, suite, unit, floor"
              data-cypress-id="billing-profile-address2"
              cypress-id="billing-profile-address2"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" required error={errors.city}>
              <Input
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="City"
                data-cypress-id="billing-profile-city"
                cypress-id="billing-profile-city"
              />
            </Field>
            <Field label={config.regionLabel} required={config.regionRequired} error={errors.stateRegion}>
              <Input
                value={form.stateRegion}
                onChange={(e) => set("stateRegion", e.target.value)}
                placeholder={config.regionLabel}
                data-cypress-id="billing-profile-state"
                cypress-id="billing-profile-state"
              />
            </Field>
          </div>
          <Field label={config.postalLabel} required={config.postalRequired} error={errors.postalCode}>
            <Input
              value={form.postalCode}
              onChange={(e) => set("postalCode", e.target.value)}
              placeholder={config.postalLabel}
              data-cypress-id="billing-profile-postal"
              cypress-id="billing-profile-postal"
            />
          </Field>
        </Section>

        <Section
          step={3}
          title="Billing contacts"
          subtitle="Who we reach about invoices, taxes and payments. The primary contact receives every invoice."
        >
          <Field label="Full name">
            <Input
              value={form.contactFullName}
              onChange={(e) => set("contactFullName", e.target.value)}
              placeholder="Full name"
              data-cypress-id="billing-profile-contact-name"
              cypress-id="billing-profile-contact-name"
            />
          </Field>
          <Field label="Email" error={errors.contactEmail}>
            <Input
              value={form.contactEmail}
              onChange={(e) => set("contactEmail", e.target.value)}
              placeholder="email@company.com"
              data-cypress-id="billing-profile-contact-email"
              cypress-id="billing-profile-contact-email"
            />
          </Field>
        </Section>

        {submitError && <p className="m-0 text-sm text-destructive">{submitError}</p>}
      </SheetBody>

      <SheetFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
          data-cypress-id="billing-profile-cancel"
          cypress-id="billing-profile-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          data-cypress-id="billing-profile-save"
          cypress-id="billing-profile-save"
        >
          {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Save Billing Profile
        </Button>
      </SheetFooter>
    </Sheet>
  );
};

const Section = ({
  step,
  title,
  subtitle,
  children,
}: {
  step: number;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ef2b55] text-xs font-semibold text-white">
        {step}
      </span>
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold text-foreground">{title}</span>
        <span className="text-sm text-muted-foreground">{subtitle}</span>
      </div>
    </div>
    {children}
  </div>
);

const Field = ({
  label,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="flex w-full flex-col gap-1.5">
    <Label>
      {label}
      {required && <span className="text-destructive"> *</span>}
    </Label>
    {children}
    {error ? (
      <span className="text-xs text-destructive">{error}</span>
    ) : hint ? (
      <span className="text-xs text-muted-foreground">{hint}</span>
    ) : null}
  </div>
);
